/**
 * KinsCircle Phase 6D — Relationship Journey Card.
 *
 * STABILITY.md §13–§14:
 *   - READ-ONLY visualization of relationship timelines from the existing
 *     Phase 6C engines. NEVER infers emotional state, relationship
 *     quality, or unstated events.
 *   - Renders null when `relationshipJourney` flag is OFF or when no
 *     relationship has the minimum 3 shared memories required for a
 *     responsibly drawn timeline.
 *   - Tapping a relative opens StoryPlaybackModal with their timeline,
 *     each slide anchored to a specific source memory.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  HeartHandshake,
  Calendar,
  Camera,
  Sparkles,
  PlayCircle,
} from 'lucide-react';
import {
  isRelationshipJourneyEnabled,
  isStoryPlaybackEnabled,
} from '@/lib/ai/featureFlag';
import {
  groupTimelineByPerson,
  type LegacyTimeline,
} from '@/lib/ai/legacyTimelineEngine';
import StoryPlaybackModal, {
  type PlaybackStory,
} from './StoryPlaybackModal';

const titleCase = (s: string): string =>
  s.replace(/\b\w/g, (c) => c.toUpperCase());

const RelationshipJourneyCard: React.FC = () => {
  if (!isRelationshipJourneyEnabled()) return null;

  const timelines = useMemo<LegacyTimeline[]>(() => {
    return groupTimelineByPerson({ maxTimelines: 8, maxSlidesPerTimeline: 12 });
  }, []);

  const [playOpen, setPlayOpen] = useState(false);
  const [activeStory, setActiveStory] = useState<PlaybackStory | null>(null);

  if (!timelines.length) return null;

  const handlePlay = (t: LegacyTimeline) => {
    if (!isStoryPlaybackEnabled()) return;
    setActiveStory({
      label: t.label,
      subtitle:
        t.yearRange.from && t.yearRange.to
          ? `Based on ${t.memoryCount} memories from ${t.yearRange.from}–${t.yearRange.to}`
          : `Based on ${t.memoryCount} memories`,
      slides: t.slides,
    });
    setPlayOpen(true);
  };

  return (
    <section className="py-12 px-4 bg-gradient-to-br from-rose-50/40 via-white to-fuchsia-50/40">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <HeartHandshake className="w-5 h-5 text-rose-600" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Relationship Journeys
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-rose-600 bg-rose-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-6 max-w-2xl">
          A read-only look at how each relative shows up across your family's
          memories — based only on tags and captions you've already saved.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {timelines.map((t) => {
            const personKey = t.bucket.replace(/^person:/, '');
            const themesAcc: Record<string, number> = {};
            for (const slide of t.slides) {
              const text = slide.body.toLowerCase();
              for (const word of [
                'birthday',
                'wedding',
                'graduation',
                'reunion',
                'cookout',
                'christmas',
                'thanksgiving',
                'beach',
                'vacation',
                'church',
                'school',
                'sports',
              ]) {
                if (text.includes(word)) {
                  themesAcc[word] = (themesAcc[word] ?? 0) + 1;
                }
              }
            }
            const themes = Object.keys(themesAcc)
              .sort((a, b) => themesAcc[b] - themesAcc[a])
              .slice(0, 3);
            const firstYear = t.yearRange.from;
            const lastYear = t.yearRange.to;
            const strongestEra =
              firstYear && lastYear
                ? `${Math.floor(((firstYear + lastYear) / 2) / 10) * 10}s`
                : null;

            return (
              <Card
                key={t.bucket}
                className="bg-white border border-rose-100 hover:border-rose-300 hover:shadow-md transition-all"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 truncate">
                      <Sparkles className="w-4 h-4 text-rose-500 shrink-0" />
                      <span className="truncate">{titleCase(personKey)}</span>
                    </span>
                    <span className="text-xs font-normal text-slate-500 shrink-0">
                      {t.memoryCount}{' '}
                      memor{t.memoryCount === 1 ? 'y' : 'ies'}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  {firstYear && lastYear && (
                    <div className="text-xs text-slate-600 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-rose-400" />
                      First shared memory: <strong>{firstYear}</strong>
                      <span className="text-slate-400">·</span>
                      Most recent: <strong>{lastYear}</strong>
                    </div>
                  )}
                  {strongestEra && (
                    <div className="text-xs text-slate-600 flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5 text-rose-400" />
                      Most photographed together in{' '}
                      <strong>the {strongestEra}</strong>
                    </div>
                  )}
                  {themes.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {themes.map((th) => (
                        <Badge
                          key={th}
                          variant="secondary"
                          className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px]"
                        >
                          {th}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {/* Mini timeline strip */}
                  <div className="flex items-center gap-1 mt-2">
                    {t.slides.slice(0, 8).map((s) => (
                      <div
                        key={s.id}
                        title={
                          (s.year ? `${s.year} — ` : '') +
                          (s.title || 'Memory')
                        }
                        className={`flex-1 h-1.5 rounded-full bg-gradient-to-r ${s.toneGradient}`}
                      />
                    ))}
                  </div>
                  {isStoryPlaybackEnabled() && (
                    <Button
                      onClick={() => handlePlay(t)}
                      size="sm"
                      variant="outline"
                      className="w-full border-rose-200 text-rose-700 hover:bg-rose-50"
                    >
                      <PlayCircle className="w-4 h-4 mr-1.5" />
                      Play their journey
                    </Button>
                  )}
                  <div className="text-[11px] text-slate-400 italic">
                    Based on {t.memoryCount} memor
                    {t.memoryCount === 1 ? 'y' : 'ies'}
                    {firstYear && lastYear
                      ? ` from ${firstYear}–${lastYear}.`
                      : '.'}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-4">
          Journeys are drawn only from the names and captions you tagged. Nothing
          about feelings, health, or events is inferred.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={activeStory}
      />
    </section>
  );
};

export default RelationshipJourneyCard;
